import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  DndContext, DragEndEvent, DragOverlay, DragStartEvent,
  PointerSensor, useSensor, useSensors, closestCorners,
  useDroppable, useDraggable,
} from '@dnd-kit/core';
import { Plus, Kanban, ChevronLeft, ChevronRight, FileText, ArrowUpRight, MessageSquare, Pencil, Trash2, ArrowRightLeft, X } from 'lucide-react';
import { useDealBoard, useMoveDealStage, useOptimisticBoardMove, useCreateDeal, useCloseDeal, useDeal, useUpdateDeal } from '@/hooks/useDeals';
import { usePipelines } from '@/hooks/usePipelines';
import { useCloseReasons } from '@/hooks/useCloseReasons';
import { useOrganizations } from '@/hooks/useOrganizations';
import { useTeamMembers } from '@/hooks/useTeam';
import { Button, Card, EmptyState, Input, Modal, SearchInput, Select, Skeleton, Textarea } from '@solid/ui';
import { Avatar } from '@/components/ui/Avatar';
import { SlidePanel } from '@/components/ui/SlidePanel';
import { BulkDeleteDialog } from '@/components/deals/BulkDeleteDialog';
import { BulkMovePipelineDialog } from '@/components/deals/BulkMovePipelineDialog';
import { formatCurrency } from '@/lib/utils';
import type { BoardDeal, BoardStage, DealPriority } from '@/types';

export default function DealBoard() {
  const [selectedPipelineId, setSelectedPipelineId] = useState<string | undefined>();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const { data: pipelines } = usePipelines();
  const { data: board, isLoading } = useDealBoard(selectedPipelineId, debouncedSearch || undefined);
  const moveStage = useMoveDealStage();
  const optimisticMove = useOptimisticBoardMove(selectedPipelineId, debouncedSearch || undefined);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);
  const [activeDeal, setActiveDeal] = useState<BoardDeal | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [closeDialog, setCloseDialog] = useState<{ dealId: string; stageId: string } | null>(null);
  const [selectedDealIds, setSelectedDealIds] = useState<Set<string>>(new Set());
  const [showBulkDelete, setShowBulkDelete] = useState(false);
  const [showBulkMove, setShowBulkMove] = useState(false);
  const [editDealId, setEditDealId] = useState<string | null>(null);

  const toggleDealSelection = (dealId: string) => {
    setSelectedDealIds((prev) => {
      const next = new Set(prev);
      if (next.has(dealId)) {
        next.delete(dealId);
      } else {
        next.add(dealId);
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedDealIds(new Set());

  const [collapsedStages, setCollapsedStages] = useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem('deal-board-collapsed');
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch {
      return new Set();
    }
  });

  const toggleCollapse = (stageId: string) => {
    setCollapsedStages((prev) => {
      const next = new Set(prev);
      if (next.has(stageId)) {
        next.delete(stageId);
      } else {
        next.add(stageId);
      }
      try {
        localStorage.setItem('deal-board-collapsed', JSON.stringify([...next]));
      } catch { /* ignore */ }
      return next;
    });
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const handleDragStart = (event: DragStartEvent) => {
    const dealId = event.active.id as string;
    for (const stage of board?.stages ?? []) {
      const found = stage.deals.find((d) => d.id === dealId);
      if (found) {
        setActiveDeal(found);
        break;
      }
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) {
      setActiveDeal(null);
      return;
    }

    const dealId = active.id as string;
    const newStageId = over.id as string;

    // Find current stage
    let currentStageId = '';
    for (const stage of board?.stages ?? []) {
      if (stage.deals.some((d) => d.id === dealId)) {
        currentStageId = stage.id;
        break;
      }
    }

    if (currentStageId === newStageId) {
      setActiveDeal(null);
      return;
    }

    // Check if target is a terminal non-won stage (needs close reason)
    const targetStage = board?.stages.find((s) => s.id === newStageId);
    if (targetStage?.isTerminal && !targetStage.isWon) {
      setActiveDeal(null);
      setCloseDialog({ dealId, stageId: newStageId });
      return;
    }

    // Optimistically move the card BEFORE clearing the overlay
    const rollback = optimisticMove(dealId, newStageId);
    setActiveDeal(null);

    // Fire the API call
    moveStage.mutate(
      { id: dealId, stageId: newStageId },
      { onError: () => rollback() }
    );
  };

  if (isLoading) {
    return (
      <div className="page-enter">
        <div className="flex items-center justify-between mb-6">
          <Skeleton className="h-8 w-48" />
        </div>
        <div className="flex gap-0 overflow-x-auto">
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="flex-shrink-0 w-64 h-[600px] rounded-none border-r border-grey-200" />
          ))}
        </div>
      </div>
    );
  }

  if (!board) return null;

  return (
    <div className="page-enter -mx-6 -mb-6">
      <div className="flex items-center justify-between mb-4 px-6">
        <div className="flex items-center gap-4">
          <h1 className="text-heading-1 text-secondary">Deals</h1>
          {pipelines && pipelines.length > 1 ? (
            <Select
              aria-label="Pipeline"
              selectSize="sm"
              value={selectedPipelineId || ''}
              onChange={(e) => setSelectedPipelineId(e.target.value || undefined)}
              options={[
                { value: '', label: pipelines.find((p) => p.isDefault)?.name || 'Default' },
                ...pipelines.filter((p) => !p.isDefault).map((p) => ({ value: p.id, label: p.name })),
              ]}
            />
          ) : (
            <span className="text-body-sm text-grey-500">{board.pipeline.name}</span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <SearchInput
            searches="deals"
            value={search}
            onChange={setSearch}
            className="w-64"
          />
          <Button onClick={() => setShowCreate(true)}>
            <Plus className="w-4 h-4" />
            Add Deal
          </Button>
        </div>
      </div>

      {selectedDealIds.size > 0 && (
        <div className="flex items-center gap-3 px-6 py-2 mb-2 mx-6 bg-accent/5 border border-accent/20 rounded-lg">
          <span className="text-sm font-medium text-secondary">
            {selectedDealIds.size} deal{selectedDealIds.size !== 1 ? 's' : ''} selected
          </span>
          <div className="flex items-center gap-2 ml-auto">
            <Button size="sm" variant="destructive" onClick={() => setShowBulkDelete(true)}>
              <Trash2 className="w-3.5 h-3.5" />
              Delete
            </Button>
            {pipelines && pipelines.length > 1 && (
              <Button size="sm" variant="outline" onClick={() => setShowBulkMove(true)}>
                <ArrowRightLeft className="w-3.5 h-3.5" />
                Move to Pipeline
              </Button>
            )}
            <button
              onClick={clearSelection}
              className="p-1 rounded hover:bg-grey-200 text-grey-400 hover:text-grey-600 ml-1"
              aria-label="Clear selection"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {board.stages.every((s) => s.deals.length === 0) ? (
        <div className="px-6">
          <Card padding="md">
            {debouncedSearch ? (
              <EmptyState
                icon={<Kanban />}
                title="No matching deals"
                description={`No deals match "${debouncedSearch}" in any stage.`}
                action={
                  <Button variant="outline" onClick={() => setSearch('')}>
                    Clear search
                  </Button>
                }
              />
            ) : (
              <EmptyState
                icon={<Kanban />}
                title="No deals yet"
                description="Create your first deal to start tracking your pipeline."
                action={
                  <Button onClick={() => setShowCreate(true)}>
                    <Plus className="w-4 h-4" />
                    New Deal
                  </Button>
                }
              />
            )}
          </Card>
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <div className="flex overflow-x-auto" style={{ height: 'calc(100vh - 180px)' }}>
            {board.stages.map((stage) => (
              <StageColumn
                key={stage.id}
                stage={stage}
                activeDealId={activeDeal?.id}
                collapsed={collapsedStages.has(stage.id)}
                onToggleCollapse={() => toggleCollapse(stage.id)}
                selectedDealIds={selectedDealIds}
                onToggleSelect={toggleDealSelection}
                onEditDeal={setEditDealId}
              />
            ))}
          </div>

          <DragOverlay dropAnimation={null}>
            {activeDeal && <DealCardContent deal={activeDeal} isDragging />}
          </DragOverlay>
        </DndContext>
      )}

      <CreateDealDialog
        open={showCreate}
        onOpenChange={setShowCreate}
        pipelineId={selectedPipelineId}
        defaultOwnerName={pipelines?.find((p) => p.id === (selectedPipelineId || pipelines?.find((p) => p.isDefault)?.id))?.defaultOwner?.name ?? undefined}
      />

      {closeDialog && (
        <CloseDealDialog
          dealId={closeDialog.dealId}
          open={!!closeDialog}
          onOpenChange={(open) => !open && setCloseDialog(null)}
        />
      )}

      <BulkDeleteDialog
        dealIds={[...selectedDealIds]}
        open={showBulkDelete}
        onOpenChange={setShowBulkDelete}
        onSuccess={clearSelection}
      />

      <BulkMovePipelineDialog
        dealIds={[...selectedDealIds]}
        currentPipelineId={board.pipeline.id}
        open={showBulkMove}
        onOpenChange={setShowBulkMove}
        onSuccess={clearSelection}
      />

      {editDealId && (
        <EditDealSidebar
          dealId={editDealId}
          open={!!editDealId}
          onOpenChange={(open) => !open && setEditDealId(null)}
        />
      )}
    </div>
  );
}

function StageColumn({
  stage,
  activeDealId,
  collapsed,
  onToggleCollapse,
  selectedDealIds,
  onToggleSelect,
  onEditDeal,
}: {
  stage: BoardStage;
  activeDealId?: string;
  collapsed: boolean;
  onToggleCollapse: () => void;
  selectedDealIds: Set<string>;
  onToggleSelect: (id: string) => void;
  onEditDeal: (dealId: string) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: stage.id });

  if (collapsed) {
    return (
      <div
        ref={setNodeRef}
        onClick={onToggleCollapse}
        className={`flex-shrink-0 w-10 h-full border-r border-grey-200 flex flex-col items-center cursor-pointer
          hover:bg-grey-100 transition-colors ${isOver ? 'bg-accent/10' : 'bg-grey-50/80'}`}
      >
        <div className="py-3 flex flex-col items-center gap-2">
          <ChevronRight className="w-3.5 h-3.5 text-grey-400" />
          <span
            className="text-xs font-semibold text-secondary whitespace-nowrap"
            style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}
          >
            {stage.name}
          </span>
          <span className="text-[10px] font-medium text-grey-400 bg-grey-200 rounded px-1 py-0.5">
            {stage.count}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={setNodeRef}
      className={`flex-shrink-0 w-64 h-full border-r border-grey-200 flex flex-col transition-colors ${isOver ? 'bg-accent/5' : 'bg-grey-50/50'}`}
    >
      {/* Column header */}
      <div className="sticky top-0 bg-white border-b border-grey-200 px-3 py-2.5 flex items-center justify-between z-10">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-secondary">{stage.name}</h3>
          <span className="text-xs font-medium text-grey-400 bg-grey-100 px-1.5 py-0.5 rounded">
            {stage.count}
          </span>
        </div>
        <button
          onClick={onToggleCollapse}
          className="p-0.5 rounded hover:bg-grey-100 text-grey-400 hover:text-grey-600 transition-colors"
          title="Collapse column"
        >
          <ChevronLeft className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Deal cards */}
      <div className="flex-1 min-h-0 p-1.5 space-y-1.5 overflow-y-auto">
        {stage.deals.map((deal) => (
          <DraggableDealCard
            key={deal.id}
            deal={deal}
            isHidden={deal.id === activeDealId}
            isSelected={selectedDealIds.has(deal.id)}
            onToggleSelect={() => onToggleSelect(deal.id)}
            onEditDeal={() => onEditDeal(deal.id)}
          />
        ))}
        {stage.count > stage.deals.length && (
          <p className="text-[11px] text-grey-400 text-center py-1.5">
            Showing {stage.deals.length} of {stage.count} — use search to find others
          </p>
        )}
      </div>

      {/* Column footer */}
      <div className="sticky bottom-0 bg-white border-t border-grey-200 px-3 py-2 text-xs text-grey-500">
        <div>{formatCurrency(stage.totalValue)} | Total amount</div>
        <div>
          {formatCurrency(stage.totalValue * (stage.winProbability / 100))}
          {' '}({stage.winProbability}%) | Weighted amount
        </div>
      </div>
    </div>
  );
}

function DraggableDealCard({
  deal,
  isHidden,
  isSelected,
  onToggleSelect,
  onEditDeal,
}: {
  deal: BoardDeal;
  isHidden?: boolean;
  isSelected?: boolean;
  onToggleSelect?: () => void;
  onEditDeal?: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: deal.id,
  });

  const style = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }
    : undefined;

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={isDragging || isHidden ? 'opacity-0 h-0 overflow-hidden' : ''}
    >
      <DealCardContent deal={deal} isSelected={isSelected} onToggleSelect={onToggleSelect} onEditDeal={onEditDeal} />
    </div>
  );
}

function DealCardContent({
  deal,
  isDragging,
  isSelected,
  onToggleSelect,
  onEditDeal,
}: {
  deal: BoardDeal;
  isDragging?: boolean;
  isSelected?: boolean;
  onToggleSelect?: () => void;
  onEditDeal?: () => void;
}) {
  const primaryContact = deal.dealContacts?.[0]?.contact;

  return (
    <div
      className={`group/card relative bg-white rounded-lg border shadow-sm cursor-grab active:cursor-grabbing
        ${isDragging ? 'shadow-lg ring-2 ring-accent/20 rotate-1 border-grey-200' : 'hover:shadow-md'}
        ${isSelected && !isDragging ? 'border-accent ring-1 ring-accent/20 bg-accent/[0.02]' : 'border-grey-200'}`}
    >
      {/* Selection checkbox — upper right, visible on hover or when selected */}
      {onToggleSelect && (
        <div className={`absolute top-2 right-2 z-10 ${isSelected ? 'opacity-100' : 'opacity-0 group-hover/card:opacity-100'} transition-opacity`}>
          <input
            type="checkbox"
            checked={isSelected}
            onChange={(e) => {
              e.stopPropagation();
              onToggleSelect();
            }}
            onPointerDown={(e) => e.stopPropagation()}
            className="rounded border-grey-300 text-accent focus:ring-accent/20 cursor-pointer"
          />
        </div>
      )}

      {/* Card body */}
      <div className="p-3">
        <Link
          to={`/admin/deals/${deal.id}`}
          onClick={(e) => isDragging && e.preventDefault()}
          className="text-sm font-medium text-accent hover:underline leading-tight line-clamp-2 block pr-6"
        >
          {deal.title}
        </Link>

        <div className="mt-2 space-y-0.5">
          <p className="text-xs text-grey-600">
            Amount: <span className="font-medium">{deal.value ? formatCurrency(Number(deal.value)) : '$0'}</span>
          </p>
          <p className="text-xs text-grey-500">
            Deal owner: {deal.owner.name || 'Unassigned'}
          </p>
        </div>

        {/* Contact avatars */}
        {(primaryContact || deal.owner) && (
          <div className="flex items-center gap-0.5 mt-2.5">
            <Avatar
              src={deal.owner.avatarUrl}
              name={deal.owner.name}
              size="xs"
            />
            {primaryContact && (
              <Avatar
                name={`${primaryContact.firstName} ${primaryContact.lastName}`}
                size="xs"
              />
            )}
          </div>
        )}

        {/* Separator + activity */}
        <div className="border-t border-grey-100 mt-2.5 pt-2">
          <p className="text-[11px] text-grey-400">
            {deal.updatedAt
              ? `Updated ${new Date(deal.updatedAt).toLocaleDateString()}`
              : `Created ${new Date(deal.createdAt).toLocaleDateString()}`}
          </p>
        </div>
      </div>

      {/* Card footer actions */}
      <div className="flex items-center justify-end gap-0.5 px-2 pb-2">
        <ActionButton icon={FileText} />
        <ActionButton icon={ArrowUpRight} />
        <ActionButton icon={MessageSquare} />
        <ActionButton icon={Pencil} onClick={onEditDeal} title="Edit deal" />
      </div>
    </div>
  );
}

function ActionButton({ icon: Icon, onClick, title }: { icon: React.ElementType; onClick?: () => void; title?: string }) {
  return (
    <button
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClick?.();
      }}
      onPointerDown={(e) => e.stopPropagation()}
      className="p-1 rounded text-accent/70 hover:text-accent hover:bg-accent/5 transition-colors"
      title={title}
    >
      <Icon className="w-3.5 h-3.5" />
    </button>
  );
}

function CreateDealDialog({
  open,
  onOpenChange,
  pipelineId,
  defaultOwnerName,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pipelineId?: string;
  defaultOwnerName?: string;
}) {
  const [form, setForm] = useState({
    title: '',
    value: '',
    organizationId: '',
    ownerId: '',
    priority: 'MEDIUM',
    description: '',
  });
  const createDeal = useCreateDeal();
  const { data: orgs } = useOrganizations();
  const { data: teamMembers } = useTeamMembers();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await createDeal.mutateAsync({
      title: form.title,
      value: form.value ? Number(form.value) : undefined,
      organizationId: form.organizationId || undefined,
      ownerId: form.ownerId || undefined,
      priority: form.priority,
      description: form.description || undefined,
      pipelineId,
    });
    onOpenChange(false);
    setForm({ title: '', value: '', organizationId: '', ownerId: '', priority: 'MEDIUM', description: '' });
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="New Deal"
      description="Create a new deal in your pipeline."
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit" form="create-deal-form" loading={createDeal.isPending}>
            Create Deal
          </Button>
        </>
      }
    >
      <form id="create-deal-form" onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Deal Title"
            required
            placeholder="e.g. Website Redesign - Acme Corp"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
          />
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Value ($)"
              type="number"
              placeholder="50000"
              value={form.value}
              onChange={(e) => setForm({ ...form, value: e.target.value })}
            />
            <Select
              label="Priority"
              value={form.priority}
              onChange={(e) => setForm({ ...form, priority: e.target.value })}
              options={[
                { value: 'LOW', label: 'Low' },
                { value: 'MEDIUM', label: 'Medium' },
                { value: 'HIGH', label: 'High' },
                { value: 'URGENT', label: 'Urgent' },
              ]}
            />
          </div>
          <Select
            label="Organization"
            value={form.organizationId}
            onChange={(e) => setForm({ ...form, organizationId: e.target.value })}
            options={[
              { value: '', label: 'None' },
              ...(orgs?.data ?? []).map((o) => ({ value: o.id, label: o.name })),
            ]}
          />
          <Select
            label="Deal Owner"
            value={form.ownerId}
            onChange={(e) => setForm({ ...form, ownerId: e.target.value })}
            options={[
              { value: '', label: defaultOwnerName ? `${defaultOwnerName} (pipeline default)` : 'Me (default)' },
              ...(teamMembers ?? []).map((m) => ({ value: m.id, label: m.name || m.email })),
            ]}
          />
          <Textarea
            label="Description"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Brief description of the deal..."
          />
      </form>
    </Modal>
  );
}

function CloseDealDialog({
  dealId,
  open,
  onOpenChange,
}: {
  dealId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [closeReasonId, setCloseReasonId] = useState('');
  const [closeNotes, setCloseNotes] = useState('');
  const { data: reasons } = useCloseReasons();
  const closeDeal = useCloseDeal();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await closeDeal.mutateAsync({
      id: dealId,
      data: { closeReasonId, closeNotes: closeNotes || undefined },
    });
    onOpenChange(false);
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Close Deal"
      description="Select a reason for closing this deal."
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit" form="close-deal-form" variant="destructive" loading={closeDeal.isPending} disabled={!closeReasonId}>
            Close Deal
          </Button>
        </>
      }
    >
      <form id="close-deal-form" onSubmit={handleSubmit} className="space-y-4">
          <Select
            label="Close Reason"
            required
            value={closeReasonId}
            onChange={(e) => setCloseReasonId(e.target.value)}
            placeholder="Select a reason..."
            options={(reasons ?? []).map((r) => ({
              value: r.id,
              label: r.label,
            }))}
          />
          <Textarea
            label="Notes (optional)"
            value={closeNotes}
            onChange={(e) => setCloseNotes(e.target.value)}
            placeholder="Additional context..."
          />
      </form>
    </Modal>
  );
}

function EditDealSidebar({
  dealId,
  open,
  onOpenChange,
}: {
  dealId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { data: deal, isLoading } = useDeal(dealId);
  const updateDeal = useUpdateDeal();
  const { data: teamMembers } = useTeamMembers();
  const [form, setForm] = useState<{
    title: string;
    value: string;
    description: string;
    priority: string;
    ownerId: string;
    dueDate: string;
  } | null>(null);

  // Initialize form when deal data loads
  if (deal && !form) {
    setForm({
      title: deal.title,
      value: deal.value ? String(Number(deal.value)) : '',
      description: deal.description || '',
      priority: deal.priority,
      ownerId: deal.ownerId,
      dueDate: deal.dueDate ? deal.dueDate.split('T')[0] : '',
    });
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form) return;
    await updateDeal.mutateAsync({
      id: dealId,
      data: {
        title: form.title,
        value: form.value ? Number(form.value) : undefined,
        description: form.description || undefined,
        priority: form.priority as DealPriority,
        ownerId: form.ownerId !== deal?.ownerId ? form.ownerId : undefined,
        dueDate: form.dueDate || undefined,
      },
    });
    onOpenChange(false);
  };

  return (
    <SlidePanel open={open} onOpenChange={onOpenChange} title="Edit Deal">
      {isLoading || !form ? (
        <div className="space-y-4">
          <div className="h-10 bg-grey-100 rounded animate-pulse" />
          <div className="h-10 bg-grey-100 rounded animate-pulse" />
          <div className="h-10 bg-grey-100 rounded animate-pulse" />
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5">
          <Input
            label="Deal Title"
            required
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
          />
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Value ($)"
              type="number"
              value={form.value}
              onChange={(e) => setForm({ ...form, value: e.target.value })}
            />
            <Select
              label="Priority"
              value={form.priority}
              onChange={(e) => setForm({ ...form, priority: e.target.value })}
              options={[
                { value: 'LOW', label: 'Low' },
                { value: 'MEDIUM', label: 'Medium' },
                { value: 'HIGH', label: 'High' },
                { value: 'URGENT', label: 'Urgent' },
              ]}
            />
          </div>
          <Select
            label="Deal Owner"
            value={form.ownerId}
            onChange={(e) => setForm({ ...form, ownerId: e.target.value })}
            options={[
              ...(teamMembers ?? []).map((m) => ({ value: m.id, label: m.name || m.email })),
              ...(!teamMembers?.find((m) => m.id === deal?.ownerId) && deal?.owner
                ? [{ value: deal.ownerId, label: deal.owner.name || 'Current Owner' }]
                : []),
            ]}
          />
          <Input
            label="Due Date"
            type="date"
            value={form.dueDate}
            onChange={(e) => setForm({ ...form, dueDate: e.target.value })}
          />
          <Textarea
            label="Description"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />

          {/* Quick info */}
          {deal && (
            <div className="pt-4 border-t border-grey-200 space-y-2">
              {deal.organization && (
                <div className="flex items-center gap-2 text-sm text-grey-500">
                  <span className="font-medium">Org:</span> {deal.organization.name}
                </div>
              )}
              {deal.stage && (
                <div className="flex items-center gap-2 text-sm text-grey-500">
                  <span className="font-medium">Stage:</span> {deal.stage.name}
                </div>
              )}
              <Link
                to={`/admin/deals/${dealId}`}
                className="text-sm text-primary hover:underline inline-flex items-center gap-1"
              >
                View Full Details <ArrowUpRight className="w-3 h-3" />
              </Link>
            </div>
          )}

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-grey-200">
            <Button variant="ghost" type="button" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" loading={updateDeal.isPending}>
              Save Changes
            </Button>
          </div>
        </form>
      )}
    </SlidePanel>
  );
}
